import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import OCRApiService from '../services/api';
import { validateSearchTerm } from '../utils/searchValidation';

// VendorLookup component for searching and selecting vendors

interface VendorMatch {
  vendor_number: string;
  vendor_name: string;
  match_score: number;
  exact_match: boolean;
  vendor_details?: any;
}

interface VendorLookupProps {
  vendorName: string;
  onVendorSelect: (vendorDetails: any) => void;
  onVendorChange?: (vendorName: string) => void;
  disabled?: boolean;
  className?: string;
}

const VendorLookup: React.FC<VendorLookupProps> = ({
  vendorName,
  onVendorSelect,
  onVendorChange,
  disabled = false,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(vendorName || '');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [vendorMatches, setVendorMatches] = useState<VendorMatch[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastLookedUpRef = useRef<string>((vendorName || '').trim());
  const lookupInFlightRef = useRef<boolean>(false);

  // Function to update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  // Update position when showing results
  useEffect(() => {
    if (showResults) {
      updateDropdownPosition();
    }
  }, [showResults, updateDropdownPosition]);

  // Update position on window resize and scroll
  useEffect(() => {
    const handleResize = () => {
      if (showResults) {
        updateDropdownPosition();
      }
    };

    const handleScroll = () => {
      if (showResults) {
        updateDropdownPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showResults, updateDropdownPosition]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideInput = inputRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideInput && !insideDropdown) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showResults]);

  // Debounced search for autocomplete
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length >= 2) {
        setIsSearching(true);
        try {
          const response = await OCRApiService.searchVendors(query, 10);
          if (response.success && response.results) {
            // Filter out any invalid vendor data
            const validResults = response.results.filter((vendor: any) => 
              vendor && vendor.name && vendor.vendor_number
            );
            setSearchResults(validResults);
            if (validResults.length > 0) {
              updateDropdownPosition();
              setShowResults(true);
            } else {
              setShowResults(false);
            }
          } else {
            setSearchResults([]);
            setShowResults(false);
          }
        } catch (error) {
          console.error('Vendor search failed:', error);
          setSearchResults([]);
          setShowResults(false);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300),
    []
  );

  // Only trigger search when user types — not on initial mount
  const isUserTyping = useRef(false);
  useEffect(() => {
    if (isUserTyping.current) {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm, debouncedSearch]);

  // Auto-lookup on mount if a vendor name was extracted — surfaces matches without requiring a click
  useEffect(() => {
    const trimmed = (vendorName || '').trim();
    if (trimmed.length >= 2) {
      handleVendorLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lookup vendor matches for the current vendor name
  const handleVendorLookup = async () => {
    const validation = validateSearchTerm(searchTerm);
    setValidationError(validation.error);
    if (!validation.isValid) return;
    const sanitizedTerm = validation.sanitized;

    // Dedupe blur+click races and repeat clicks for the same term
    if (lookupInFlightRef.current && lastLookedUpRef.current === sanitizedTerm) return;

    lookupInFlightRef.current = true;
    lastLookedUpRef.current = sanitizedTerm;
    setIsLookingUp(true);
    // Clear selected vendor when performing a new lookup
    setSelectedVendor(null);
    
    try {
      // searchMultipleVendors now accepts a single string or array
      // It will search for the single vendor name and return results
      const response = await OCRApiService.searchMultipleVendors(sanitizedTerm);
      
      // Handle response format - could be results array or object with results
      let matches: any[] = [];
      if (response.results && Array.isArray(response.results)) {
        // If results is an array, it might contain objects with company_name and match info
        matches = response.results.map((result: any) => {
          // Transform API response to VendorMatch format
          if (result.exact_match || result.match_score) {
            return {
              vendor_number: result.vendor_number || result.match.vendorNumber || '',
              vendor_name:  result.vendor_name || searchTerm,
              match_score: result.confidence || result.match_score || 0,
              exact_match: result.exact_match === true && (result.match_score || 0) >= 0.95,
              vendor_details: result.vendor_details
            };
          }
          return null;
        }).filter((match: any) => match !== null);
      } else if (Array.isArray(response)) {
        // If response is directly an array
        matches = response;
      }
      setVendorMatches(matches);
    } catch (error) {
      console.error('Vendor lookup failed:', error);
      setVendorMatches([]);
    } finally {
      setIsLookingUp(false);
      lookupInFlightRef.current = false;
    }
  };

  const handleSearchTermChange = (value: string) => {
    isUserTyping.current = true;
    setSearchTerm(value);
    setValidationError(null);
    if (onVendorChange) {
      onVendorChange(value);
    }

    // Clear selected vendor if the search term no longer matches the selected vendor name
    if (selectedVendor && selectedVendor.name !== value) {
      setSelectedVendor(null);
    }

    setVendorMatches([]);
  };

  const handleVendorSelection = (vendor: any) => {
    if (!vendor || !vendor.name) {
      console.error('Invalid vendor data:', vendor);
      return;
    }

    isUserTyping.current = false;
    lastLookedUpRef.current = (vendor.name || '').trim();
    setSelectedVendor(vendor);
    setSearchTerm(vendor.name);
    setSearchResults([]);
    setShowResults(false);
    setVendorMatches([]);
    onVendorSelect(vendor);
    if (onVendorChange) {
      onVendorChange(vendor.name);
    }
  };

  const handleMatchSelection = (match: VendorMatch) => {
    if (match.vendor_details) {
      handleVendorSelection({
        ...match.vendor_details,
        vendor_number: match.vendor_number,
        name: match.vendor_name
      });
    }
  };

  return (
    <div className={`vendor-lookup ${className}`}>
      <div className="flex gap-2">
        <div className="flex-1 relative z-30">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchTermChange(e.target.value)}
            onFocus={() => {
              setShowResults(searchResults.length > 0);
              updateDropdownPosition();
            }}
            onBlur={() => {
              setTimeout(() => setShowResults(false), 200);
              const trimmed = searchTerm.trim();
              if (
                trimmed.length >= 2 &&
                !lookupInFlightRef.current &&
                !(selectedVendor && selectedVendor.name === searchTerm)
              ) {
                handleVendorLookup();
              }
            }}
            placeholder="Enter vendor name..."
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
          />
          
          {/* Portal-based Search Results Dropdown */}
          {showResults && searchResults.length > 0 && createPortal(
            <div
              ref={dropdownRef}
              className="fixed bg-white border-2 border-gray-300 rounded-md shadow-2xl max-h-60 overflow-y-auto"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 99999
              }}
            >
              {searchResults.filter(vendor => vendor && vendor.name).map((vendor, index) => (
                <div
                  key={vendor.vendor_number || index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleVendorSelection(vendor)}
                  className="px-3 py-2 bg-white hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 truncate">{vendor.name}</div>
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <span>Vendor #{vendor.vendor_number}</span>
                      {vendor['1099_type'] && vendor['1099_type'].trim() && (
                        <span className="text-xs text-brand-600 bg-brand-50 px-2 py-1 rounded">
                          {vendor['1099_type']}
                        </span>
                      )}
                    </div>
                    {(vendor.address || vendor.city || vendor.state || vendor.zip) && (
                      <div className="text-xs text-gray-500 mt-1">
                        {vendor.address && <span>{vendor.address}</span>}
                        {(vendor.city || vendor.state || vendor.zip) && (
                          <span>
                            {vendor.address && ', '}
                            {vendor.city}{vendor.city && vendor.state && ', '}{vendor.state} {vendor.zip}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}
          
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
            </div>
          )}
        </div>
        
        <button
          onClick={handleVendorLookup}
          disabled={disabled || isLookingUp || !searchTerm.trim()}
          className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLookingUp ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Looking up...
            </>
          ) : (
            'Lookup'
          )}
        </button>
      </div>

      {/* Validation Error */}
      {validationError && (
        <p className="mt-1 text-sm text-red-600">{validationError}</p>
      )}

      {/* Vendor Matches */}
      {vendorMatches.length > 0 && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Potential matches found:
          </h4>
          <div className="space-y-2">
            {vendorMatches.map((match, index) => (
              <div
                key={index}
                onClick={() => handleMatchSelection(match)}
                className="p-2 bg-white rounded border hover:bg-brand-50 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {match.vendor_name} 
                    </div>
                    <div className="text-sm text-gray-600">
                      Vendor #{match.vendor_number}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      match.exact_match ? 'text-green-600' : 
                      match.match_score > 0.8 ? 'text-brand-600' : 'text-yellow-600'
                    }`}>
                      {match.exact_match ? 'Exact Match' : 
                       `${Math.round(match.match_score * 100)}% match`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Vendor Info */}
      {selectedVendor && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-green-800">
              Vendor Selected
            </span>
          </div>
          <div className="text-sm text-green-700">
            <div><strong>Name:</strong> {selectedVendor.name}</div>
            <div><strong>Number:</strong> {selectedVendor.vendor_number}</div>
            {selectedVendor['1099_type'] && (
              <div><strong>1099 Type:</strong> {selectedVendor['1099_type']}</div>
            )}
            {(selectedVendor.address || selectedVendor.city || selectedVendor.state || selectedVendor.zip) && (
              <div><strong>Address:</strong> 
                {selectedVendor.address && <span>{selectedVendor.address}</span>}
                {(selectedVendor.city || selectedVendor.state || selectedVendor.zip) && (
                  <span>
                    {selectedVendor.address && ', '}
                    {selectedVendor.city}{selectedVendor.city && selectedVendor.state && ', '}{selectedVendor.state} {selectedVendor.zip}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No matches message */}
      {vendorMatches.length === 0 && searchTerm && !isLookingUp && !selectedVendor && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">
            No matches found for "{searchTerm}". You can still proceed with the current vendor name.
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default VendorLookup;
